import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import { Storage } from 'site-modules/shared/utils/storage';
import { injectScript } from 'client/utils/inject-script';
import {
  auth,
  authProvider,
  firebaseCredentials,
  fireFirebaseTracking,
  getCurrentUser,
  onReject,
} from 'site-modules/shared/components/profile/firebase-auth';
import { getSocialProfile } from 'site-modules/shared/components/profile/idm/idm-utils';
import {
  displayGoogleOneTap,
  successSocialAuthAction,
} from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { VisitorModel } from 'client/data/models/visitor';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';

const CREATIVE_ID = 'google_one_tap';
const GOOGLE_ONE_TAP_SHOWN = 'google_one_tap_shown';
const ATTEMPT_DISPLAY_GOOGLE_AUTHENTICATE = 'attempt_display_google_authenticate';
// https://contextify.dev/blog/google-one-tap-login-firebase/
const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';

function GoogleOneTapUI({
  isAuthenticated,
  isAnonymousUser,
  disableGoogleOneTap,
  idmData,
  updateIdmDataOnLogin,
  signIn,
  privacyAccepted,
}) {
  const sessionStorage = useRef(new Storage('sessionStorage'));

  useEffect(() => {
    const googleOneTapAvailable =
      isAuthenticated !== null && (!isAuthenticated || (isAnonymousUser && !isEmpty(idmData))) && !disableGoogleOneTap;
    const googleOneTapSkip = !privacyAccepted;

    const showGoogleOneTap = sessionStorage.current.get(GOOGLE_ONE_TAP_SHOWN);

    if (!showGoogleOneTap && googleOneTapAvailable && !googleOneTapSkip) {
      sessionStorage.current.set(GOOGLE_ONE_TAP_SHOWN, true);
      injectScript(GOOGLE_GSI_SRC)
        .then(() => {
          displayGoogleOneTap(ATTEMPT_DISPLAY_GOOGLE_AUTHENTICATE);
          const firebase = firebaseCredentials();
          window.google.accounts.id.initialize({
            client_id: firebase.googleId,
            callback: ({ credential }) =>
              getCurrentUser().then(usr => {
                const uid = usr?.uid;
                return auth()
                  .signInWithCredential(authProvider().GoogleAuthProvider.credential(credential))
                  .then(() => {
                    fireFirebaseTracking('SIGN_IN_SOCIAL_CREATIVE_ID');
                    successSocialAuthAction({
                      creative_id: CREATIVE_ID,
                      value: 'google',
                    });
                    return getSocialProfile(idmData, uid);
                  })
                  .then(({ profile, aid }) => updateIdmDataOnLogin(profile, aid, true))
                  .then(() => signIn(false))
                  .catch(error => {
                    onReject(error, CREATIVE_ID);
                  });
              }),
            auto_select: false,
          });
          window.google.accounts.id.prompt();
        })
        .catch(error => {
          onReject(error, CREATIVE_ID);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnonymousUser, isAuthenticated, privacyAccepted, idmData]);
  return null;
}

GoogleOneTapUI.propTypes = {
  signIn: PropTypes.func.isRequired,
  updateIdmDataOnLogin: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isAnonymousUser: PropTypes.bool,
  idmData: PropTypes.shape({}),
  disableGoogleOneTap: PropTypes.bool,
  privacyAccepted: PropTypes.bool,
};

GoogleOneTapUI.defaultProps = {
  isAuthenticated: false,
  isAnonymousUser: false,
  idmData: null,
  disableGoogleOneTap: false,
  privacyAccepted: false,
};

const mapStateToProps = ({ profile, featureFlags }) => ({
  isAuthenticated: profile?.isAuthenticated,
  isAnonymousUser: profile?.isAnonymousUser,
  disableGoogleOneTap: !!featureFlags?.['disable-google-one-tap'],
});

const stateToPropsConfig = {
  idmData: bindToPath(props => (props.isAuthenticated ? 'data.idm' : ''), UserProfileModel),
  privacyAccepted: bindToPath('privacyAccepted', VisitorModel),
};

export const GoogleOneTap = insiderMethods(
  connect(mapStateToProps)(connectToModel(GoogleOneTapUI, stateToPropsConfig))
);
